// EditFlow.js
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar7 from "../components/Navbar7";
import { retrieveViews, getFlow, updateFlow } from "../services/database";


const EditFlow = () => {
  
  const location = useLocation();
  const navigate = useNavigate();

  const { projectId, flowId } = location.state || {};

  const [views, setViews] = useState([]);
  const [flowItems, setFlowItems] = useState([]);
  const [flowTitle, setFlowTitle] = useState("");
  const [draggingViewId, setDraggingViewId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  // Fetch the flow and views data on component mount
  useEffect(() => {
    if (!projectId || !flowId) {
      console.error("Project ID or Flow ID is missing");
      navigate("/projects");
      return;
    }

    const fetchData = async () => {
      setIsFetching(true);
      try {
        // Fetch the flow details
        const flowResponse = await getFlow(flowId);
        if (!flowResponse || flowResponse.status !== "success") {
          throw new Error("Failed to fetch flow data");
        }

        const flow = flowResponse.flow;
        setFlowTitle(flow.title || "Untitled Flow");

        // Fetch all views for this project
        const fetchedViews = await retrieveViews(projectId);

        // Filter out results so only versions with revisionStatus === "complete" or no revisionStatus
        // Then sort them by time_created (ascending) and label them with versionIndex
        const validViews = fetchedViews
          .map((view) => {
            const validVersions = (view.versions || []).filter(
              (ver) => !ver.revisionStatus || ver.revisionStatus === "complete"
            );
            if (validVersions.length === 0) {
              return null;
            }
            // Sort versions by time_created ascending
            validVersions.sort((a, b) => {
              const timeA = new Date(a.time_created || new Date());
              const timeB = new Date(b.time_created || new Date());
              return timeA - timeB; // oldest to newest
            });
            // Assign versionIndex (1-based), oldest is version 1, newest is version n
            validVersions.forEach((ver, idx) => {
              ver.versionIndex = idx + 1;
            });

            // Find the version that's in the flow (if any)
            const flowViewItem = flow.order?.find(item => item.viewId === view._id);
            const selectedVersionId = flowViewItem?.versionId || validVersions[validVersions.length - 1]?.versionId || "";

            return {
              ...view,
              versions: validVersions,
              selectedVersionId,
              // Flag if this view is already in the flow
              isInFlow: !!flowViewItem
            };
          })
          .filter(Boolean);

        setViews(validViews);

        // Set up flow items from the flow's order
        if (flow.order && Array.isArray(flow.order)) {
          const items = flow.order.map(item => {
            const view = fetchedViews.find(v => v._id === item.viewId);
            if (!view) return null;

            const version = view.versions?.find(v => v.versionId === item.versionId);
            const versionIndex = version ? 
              (view.versions || []).findIndex(v => v.versionId === item.versionId) + 1 : 
              1;

            return {
              viewId: item.viewId,
              title: view.title || "Untitled View",
              versionId: item.versionId,
              versionIndex: versionIndex,
              orderId: item.orderId
            };
          }).filter(Boolean);

          // Sort by orderId
          items.sort((a, b) => a.orderId - b.orderId);
          setFlowItems(items);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        alert("Failed to load flow data. Please try again.");
        navigate("/project-details", { state: { projectId } });
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();
  }, [projectId, flowId, navigate]);

  // Handle version change in the left column's <select>
  const handleVersionChange = (viewId, newVersionId) => {
    // Update the views array
    setViews((prev) =>
      prev.map((v) => {
        if (v._id === viewId) {
          return {
            ...v,
            selectedVersionId: newVersionId
          };
        }
        return v;
      })
    );

    // If this view is already in flowItems, update that as well
    setFlowItems((prev) => {
      const updatedItems = prev.map((item) => {
        if (item.viewId === viewId) {
          // Find the version index
          const view = views.find(v => v._id === viewId);
          const version = view?.versions?.find(ver => ver.versionId === newVersionId);
          const versionIndex = version?.versionIndex || 1;
          
          return {
            ...item,
            versionId: newVersionId,
            versionIndex
          };
        }
        return item;
      });
      return updatedItems;
    });
  };

  // Drag and drop event handlers
  const handleDragStart = (e, viewId) => {
    setDraggingViewId(viewId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!draggingViewId) return;

    const viewObj = views.find((v) => v._id === draggingViewId);
    if (!viewObj) return;

    // Check if already in flow
    const alreadyInFlow = flowItems.some((item) => item.viewId === draggingViewId);
    if (alreadyInFlow) {
      return; // do nothing if it is already present
    }

    const versionId =
      viewObj.selectedVersionId || viewObj.versions[viewObj.versions.length - 1].versionId;
    const selectedVersion = viewObj.versions.find(
      (ver) => ver.versionId === versionId
    );

    setFlowItems((prev) => {
      const newItems = [
        ...prev,
        {
          viewId: viewObj._id,
          title: viewObj.title,
          versionId,
          versionIndex: selectedVersion?.versionIndex || 1,
          orderId: prev.length + 1
        }
      ];
      return newItems;
    });

    // Mark this view as being in the flow
    setViews(prev => 
      prev.map(v => 
        v._id === draggingViewId ? { ...v, isInFlow: true } : v
      )
    );

    setDraggingViewId(null);
  };

  const handleAddToFlow = (view) => {
    const alreadyInFlow = flowItems.some((item) => item.viewId === view._id);
    if (alreadyInFlow) {
      return;
    }

    const versionId = view.selectedVersionId || view.versions[view.versions.length - 1].versionId;
    const selectedVersion = view.versions.find((ver) => ver.versionId === versionId);

    setFlowItems((prev) => {
      const newItems = [
        ...prev,
        {
          viewId: view._id,
          title: view.title,
          versionId,
          versionIndex: selectedVersion?.versionIndex || 1,
          orderId: prev.length + 1
        }
      ];
      return newItems;
    });

    // Mark this view as being in the flow
    setViews(prev => 
      prev.map(v => 
        v._id === view._id ? { ...v, isInFlow: true } : v
      )
    );
  };

  // Updated handleMoveUp function with proper orderId management
  const handleMoveUp = (index) => {
    if (index === 0) return;
    
    setFlowItems((prev) => {
      const newArray = [...prev];
      
      // Swap positions in array
      const tempItem = newArray[index - 1];
      newArray[index - 1] = newArray[index];
      newArray[index] = tempItem;
      
      // Recalculate orderIds to ensure they're sequential
      return newArray.map((item, idx) => ({
        ...item,
        orderId: idx + 1
      }));
    });
  };

  // Updated handleMoveDown function with proper orderId management
  const handleMoveDown = (index) => {
    if (index === flowItems.length - 1) return;
    
    setFlowItems((prev) => {
      const newArray = [...prev];
      
      // Swap positions in array
      const tempItem = newArray[index + 1];
      newArray[index + 1] = newArray[index];
      newArray[index] = tempItem;
      
      // Recalculate orderIds to ensure they're sequential
      return newArray.map((item, idx) => ({
        ...item,
        orderId: idx + 1
      }));
    });
  };

  const handleRemove = (index, viewId) => {
    setFlowItems((prev) => {
      const newArray = [...prev];
      newArray.splice(index, 1);
      
      // Recalculate orderIds to ensure they're sequential
      return newArray.map((item, idx) => ({
        ...item,
        orderId: idx + 1
      }));
    });

    // Mark this view as no longer in the flow
    setViews(prev => 
      prev.map(v => 
        v._id === viewId ? { ...v, isInFlow: false } : v
      )
    );
  };

  const handleSaveFlow = async () => {
    if (!flowTitle.trim()) {
      alert("Please enter a flow title.");
      return;
    }

    if (flowItems.length < 2) {
      alert("Please add at least two views.");
      return;
    }

    try {
      setIsLoading(true);
      
      // Prepare the updated flow data
      const updatedFlow = {
        projectId,
        title: flowTitle.trim(),
        order: flowItems.map(item => ({
          viewId: item.viewId,
          versionId: item.versionId,
          orderId: item.orderId
        }))
      };
      
      // Use the updateFlow function to update the flow
      const response = await updateFlow(flowId, updatedFlow);

      if (response && response.status === "success") {
        navigate("/project-details", {
          state: {
            projectId
          }
        });
      } else {
        alert("Failed to update flow.");
      }
    } catch (error) {
      console.error("Error updating flow:", error);
      alert("Error updating flow. Check console.");
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state
  if (isFetching) {
    return (
      <div className="min-h-screen font-sans antialiased bg-white">
        <Navbar7 backPath="/project-details" projectId={projectId} />
        <div className="container mx-auto p-6">
          <div className="flex justify-center items-center h-64">
            <p className="text-lg text-gray-600">Loading flow data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-sans antialiased bg-white">
      <Navbar7 backPath="/project-details" projectId={projectId} title="Edit Flow"/>

      <div className="container mx-auto p-6">
        {/* Mobile view - Stacked vertically */}
        <div className="flex flex-col md:hidden space-y-6">
          {/* Views Section */}
          <div
            id="views-section"
            className="w-full border p-4 rounded shadow-sm"
          >
            <h2 className="text-xl font-semibold mb-4">Views</h2>
            <div id="views-list" className="space-y-4">
              {views.length === 0 ? (
                <p className="text-gray-600">No valid views found.</p>
              ) : (
                views.map((view) => (
                  <div
                    key={view._id}
                    className={`view-card p-4 border rounded flex justify-between items-center ${
                      view.isInFlow ? "bg-gray-100" : "bg-slate-100"
                    }`}
                    data-view-id={view._id}
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, view._id)}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{view.title}</span>
                      {/* Version dropdown */}
                      <div className="inline-block">
                        <select
                          className="version-select mt-2 text-sm text-slate-500"
                          value={view.selectedVersionId}
                          onChange={(e) => handleVersionChange(view._id, e.target.value)}
                        >
                          {view.versions
                            .slice()
                            .sort((a, b) => b.versionIndex - a.versionIndex) // sort descending: most recent first
                            .map((ver) => (
                              <option key={ver.versionId} value={ver.versionId}>
                                Version {ver.versionIndex}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    <button
                      className={`move-to-flow text-slate-900 hover:text-slate-700 ${
                        view.isInFlow ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      title={view.isInFlow ? "Already in flow" : "Add to Flow"}
                      onClick={() => !view.isInFlow && handleAddToFlow(view)}
                      disabled={view.isInFlow}
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
          
          {/* Flow Section */}
          <div
            id="flow-section"
            className="w-full border p-4 rounded shadow-sm"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <h2 className="text-xl font-semibold mb-4">Flow</h2>

            <div className="mb-4">
              <input
                id="flow-title"
                type="text"
                required
                placeholder="Enter flow title"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-slate-700"
                value={flowTitle}
                onChange={(e) => setFlowTitle(e.target.value)}
              />
            </div>

            <div
              id="flow-list"
              className="min-h-[200px] space-y-4 border p-2 rounded"
            >
              {flowItems.length === 0 ? (
                <p className="text-gray-500 p-4 text-center">
                  Drag views here to add them to the flow
                </p>
              ) : (
                flowItems.map((item, index) => (
                  <div
                    key={`${item.viewId}-${index}`}
                    className="p-4 border rounded bg-white flex flex-col md:flex-row md:items-center md:justify-between"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-800">
                        {item.title} (Version {item.versionIndex})
                      </span>
                    </div>

                    <div className="flex gap-2 mt-2 md:mt-0">
                      <button
                        onClick={() => handleMoveUp(index)}
                        className="text-slate-900 hover:text-slate-700"
                        title="Move Up"
                        disabled={index === 0}
                      >
                        <svg
                          className={`w-5 h-5 ${index === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleMoveDown(index)}
                        className="text-slate-900 hover:text-slate-700"
                        title="Move Down"
                        disabled={index === flowItems.length - 1}
                      >
                        <svg
                          className={`w-5 h-5 ${index === flowItems.length - 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleRemove(index, item.viewId)}
                        className="text-red-900 hover:text-red-700"
                        title="Remove from Flow"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <button
              id="save-flow"
              className="mt-4 w-full bg-slate-900 text-white py-2 rounded hover:bg-slate-700 transition-colors disabled:opacity-50"
              onClick={handleSaveFlow}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save Flow"}
            </button>
          </div>
        </div>

        {/* Desktop view - Side by side */}
        <div className="hidden md:flex md:flex-row md:gap-6">
          {/* Views Column */}
          <div
            id="views-section"
            className="w-1/2 border p-4 rounded shadow-sm"
          >
            <h2 className="text-xl font-semibold mb-4">Views</h2>
            <div id="views-list" className="space-y-4">
              {views.length === 0 ? (
                <p className="text-gray-600">No valid views found.</p>
              ) : (
                views.map((view) => (
                  <div
                    key={view._id}
                    className={`view-card p-4 border rounded flex justify-between items-center ${
                      view.isInFlow ? "bg-gray-100" : "bg-slate-100"
                    }`}
                    data-view-id={view._id}
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, view._id)}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{view.title}</span>
                      {/* Version dropdown */}
                      <div className="inline-block">
                        <select
                          className="version-select mt-2 text-sm text-slate-500"
                          value={view.selectedVersionId}
                          onChange={(e) => handleVersionChange(view._id, e.target.value)}
                        >
                          {view.versions
                            .slice()
                            .sort((a, b) => b.versionIndex - a.versionIndex) // sort descending: most recent first
                            .map((ver) => (
                              <option key={ver.versionId} value={ver.versionId}>
                                Version {ver.versionIndex}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    <button
                      className={`move-to-flow text-slate-900 hover:text-slate-700 ${
                        view.isInFlow ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      title={view.isInFlow ? "Already in flow" : "Add to Flow"}
                      onClick={() => !view.isInFlow && handleAddToFlow(view)}
                      disabled={view.isInFlow}
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Flow Column */}
          <div
            id="flow-section"
            className="w-1/2 border p-4 rounded shadow-sm"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <h2 className="text-xl font-semibold mb-4">Flow</h2>

            <div className="mb-4">
              <input
                id="flow-title"
                type="text"
                required
                placeholder="Enter flow title"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-slate-700"
                value={flowTitle}
                onChange={(e) => setFlowTitle(e.target.value)}
              />
            </div>

            <div
              id="flow-list"
              className="min-h-[200px] space-y-4 border p-2 rounded"
            >
              {flowItems.length === 0 ? (
                <p className="text-gray-500 p-4 text-center">
                  Drag views here to add them to the flow
                </p>
              ) : (
                flowItems.map((item, index) => (
                  <div
                    key={`${item.viewId}-${index}`}
                    className="p-4 border rounded bg-white flex flex-col md:flex-row md:items-center md:justify-between"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-800">
                        {item.title} (Version {item.versionIndex})
                      </span>
                    </div>

                    <div className="flex gap-2 mt-2 md:mt-0">
                      <button
                        onClick={() => handleMoveUp(index)}
                        className="text-slate-900 hover:text-slate-700"
                        title="Move Up"
                        disabled={index === 0}
                      >
                        <svg
                          className={`w-5 h-5 ${index === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleMoveDown(index)}
                        className="text-slate-900 hover:text-slate-700"
                        title="Move Down"
                        disabled={index === flowItems.length - 1}
                      >
                        <svg
                          className={`w-5 h-5 ${index === flowItems.length - 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleRemove(index, item.viewId)}
                        className="text-red-900 hover:text-red-700"
                        title="Remove from Flow"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <button
              id="save-flow"
              className="mt-4 w-full bg-slate-900 text-white py-2 rounded hover:bg-slate-700 transition-colors disabled:opacity-50"
              onClick={handleSaveFlow}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save Flow"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditFlow;